import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useGetUserID } from "../hooks/useGetUserID";
import ServingSizeSelector from '../components/ServingSizeSelector';
import RecipePriceDetails from '../components/RecipePriceDetails';
import { API_BASE_URL } from '../config';
import RecipeImage from '../components/RecipeImage';
import { PlatformWrapper } from '../components/PlatformWrapper';
import { useCookies } from 'react-cookie';
import PlusIcon from '../components/PlusIcon';
import ProfilePicture from '../components/ProfilePicture';
import '../styles/recipedetails.css';
import '../styles/instructional-roadmap.css';
import '../styles/recipedetails-modern.css';
import RecipeSuggestions from '../components/RecipeSuggestions';
import RecipeBuddy from '../components/RecipeBuddy';
import LoadingSpinner from '../components/LoadingSpinner';
import { calculateRecipeNutrition, formatNutritionValue } from '../utils/nutritionCalculator';
import SaveOptionsPopup from '../components/SaveOptionsPopup';
import SignInPopup from '../components/SignInPopup';
import { useSignInPopup } from '../hooks/useSignInPopup';


export default function RecipeDetails() {
    const { recipeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const userID = useGetUserID();
    const [recipe, setRecipe] = useState(null);
    const [savedRecipes, setSavedRecipes] = useState([]);
    const [mealPrepDraftIds, setMealPrepDraftIds] = useState([]);
    const [showSaveOptionsPopup, setShowSaveOptionsPopup] = useState(false);
    const [servingSizes, setServingSizes] = useState({});
    const [recipeCosts, setRecipeCosts] = useState({});
    const [ingredientPrices, setIngredientPrices] = useState({});
    const [error, setError] = useState(null);
    const [cookies] = useCookies(["access_token"]);
    const [isSaving, setIsSaving] = useState(false);
    
    const [loadingCost, setLoadingCost] = useState(false);
    const recipePriceDetailsRef = useRef(null);
    const [showCosts, setShowCosts] = useState(true); // State to control cost visibility
    
    // Nutritional data state
    const [nutritionData, setNutritionData] = useState(null);
    const [loadingNutrition, setLoadingNutrition] = useState(false);
    const [showNutrition, setShowNutrition] = useState(false); // Collapsed by default
    const [showNutritionBreakdown, setShowNutritionBreakdown] = useState(false); // Collapsed by default
    
    // Collapsible sections state
    const [showIngredients, setShowIngredients] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);
    
    // Modal state for image viewing
    const [showImageModal, setShowImageModal] = useState(false);
    const [modalImageUrl, setModalImageUrl] = useState('');
    const [modalImageAlt, setModalImageAlt] = useState('');
    
    // Cupboard state - track loading and success states per ingredient
    const [addingToCupboard, setAddingToCupboard] = useState({});
    const [addedToCupboard, setAddedToCupboard] = useState({});
    const [checkingCupboard, setCheckingCupboard] = useState(false);

    // Helper to get auth token from cookies or localStorage
    const getAuthToken = () => {
        return cookies.access_token || window.localStorage.getItem('auth_token');
    };

    // Helper to build Authorization header only when token exists
    const getAuthHeaders = () => {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const isAuthenticated = !!(userID && getAuthToken());
    const { showPopup: showSignInPopup, handleClosePopup: handleCloseSignInPopup, openPopup: openSignInPopup } = useSignInPopup(isAuthenticated, 'recipedetails', { skipAutoShow: true });
    const [signInPopupFromFrankie, setSignInPopupFromFrankie] = useState(false);

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/recipes/${recipeId}`, {
                    headers: getAuthHeaders(),
                });
                console.log('Recipe data:', response.data);
                setRecipe(response.data);
                setServingSizes(prev => ({
                    ...prev,
                    [recipeId]: response.data.servings
                }));
                setError(null);
            } catch (error) {
                console.error("Error fetching recipe:", error);
                if (error.response?.status === 404 && !getAuthToken()) {
                    navigate(`/auth?redirect=${encodeURIComponent(`/recipe/${recipeId}`)}`, { replace: true });
                    return;
                }
                setError(error.response?.data?.message || "Error fetching recipe details");
            }
        };

        const fetchSavedRecipes = async () => {
            try {
                if (!userID) return;
                const response = await axios.get(`${API_BASE_URL}/recipes/savedRecipes/ids/${userID}`, {
                    headers: getAuthHeaders()
                });
                const ids = response.data.savedRecipes || [];
                setSavedRecipes(ids.map(id => id.toString()));
            } catch (error) {
                console.error("Error fetching saved recipes:", error);
            }
        };

        const fetchMealPrepDraftIds = async () => {
            try {
                if (!userID) return;
                const response = await axios.get(`${API_BASE_URL}/recipes/meal-prep-draft/ids/${userID}`, {
                    headers: getAuthHeaders()
                });
                const ids = response.data.mealPrepDraft || [];
                setMealPrepDraftIds(ids.map(id => id.toString()));
            } catch (error) {
                console.error("Error fetching meal prep draft:", error);
            }
        };

        fetchRecipe();
        if (userID) {
            fetchSavedRecipes();
            fetchMealPrepDraftIds();
        }
    }, [recipeId, userID]);

    // Check which ingredients are already in the cupboard
    useEffect(() => {
        const checkCupboardIngredients = async () => {
            if (!recipe || !recipe.ingredients || !userID || !getAuthToken()) {
                return;
            }

            try {
                setCheckingCupboard(true);
                
                // Get ingredient names from the recipe
                const ingredientNames = recipe.ingredients.map(ing => ing.name);
                
                // Use the match-ingredients endpoint to check which are in cupboard
                const response = await axios.post(
                    `${API_BASE_URL}/cupboard/match-ingredients`,
                    { ingredientNames },
                    {
                        headers: {
                            ...getAuthHeaders(),
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                if (response.data.success && response.data.matches) {
                    // Create a map of which ingredients are in the cupboard
                    const alreadyInCupboard = {};
                    recipe.ingredients.forEach((ingredient, index) => {
                        const match = response.data.matches.find(
                            m => m.requestedName.toLowerCase() === ingredient.name.toLowerCase()
                        );
                        if (match && match.available) {
                            alreadyInCupboard[index] = true;
                        }
                    });
                    
                    // Update the addedToCupboard state
                    setAddedToCupboard(alreadyInCupboard);
                }
            } catch (error) {
                console.error("Error checking cupboard ingredients:", error);
                // Silently fail - this is not critical functionality
                // If it's a 401, user might not be logged in, which is fine
            } finally {
                setCheckingCupboard(false);
            }
        };

        checkCupboardIngredients();
    }, [recipe, recipeId, userID, cookies.access_token]);

    // Fetch nutritional data when recipe or serving size changes
    useEffect(() => {
        const fetchNutrition = async () => {
            if (!recipe || !recipe.ingredients || recipe.ingredients.length === 0) return;
            
            try {
                setLoadingNutrition(true);
                const currentServings = servingSizes[recipeId] || recipe.servings || 1;
                const scaledIngredients = recipe.ingredients.map(ing => ({
                    ...ing,
                    amount: parseFloat(((ing.amount * currentServings) / recipe.servings).toFixed(2))
                }));
                
                const nutrition = await calculateRecipeNutrition(
                    recipeId,
                    scaledIngredients,
                    currentServings
                );
                setNutritionData(nutrition);
            } catch (error) {
                console.error('Error fetching nutrition for recipe:', recipeId, error);
                // Silently fail - nutrition is optional
            } finally {
                setLoadingNutrition(false);
            }
        };
        
        fetchNutrition();
    }, [recipe, recipeId, servingSizes]);

    // Simple function to scroll to Recipe Cost Calculator header
    const scrollToCostCalculator = (attempt = 0) => {
        const maxAttempts = 15;
        
        // Try multiple selectors to find the header
        const header = document.getElementById('recipe-cost-calculator-header') ||
                      document.querySelector('.cost-header') ||
                      document.querySelector('.modern-cost-section .cost-header');
        
        if (header) {
            // Use scrollIntoView for reliable scrolling
            header.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
            });
            
            console.log('✅ Scrolled to Recipe Cost Calculator header', {
                element: header,
                scrollY: window.pageYOffset
            });
            return true;
        } else if (attempt < maxAttempts) {
            // Retry if element not found yet
            setTimeout(() => {
                scrollToCostCalculator(attempt + 1);
            }, 150);
            return false;
        } else {
            console.log('⚠️ Could not find Recipe Cost Calculator header after', maxAttempts, 'attempts');
            return false;
        }
    };

    // Check for showCosts URL parameter and auto-fetch costs if needed
    useEffect(() => {
        const showCostsParam = searchParams.get('showCosts');
        if (showCostsParam === 'true' && recipe && !loadingCost && !recipeCosts.hasOwnProperty(recipeId)) {
            console.log('Auto-fetching costs based on URL parameter');
            
            // Inline the fetch logic to avoid dependency issues
            const autoFetchCosts = async () => {
                try {
                    setLoadingCost(true);
                    
                    const currentServings = servingSizes[recipeId] || recipe.servings;
                    const scaledIngredients = recipe.ingredients.map(ing => ({
                        ...ing,
                        amount: parseFloat(((ing.amount * currentServings) / recipe.servings).toFixed(2))
                    }));

                    const preferredRegion = window.localStorage.getItem('preferredRegion') || null;
                    const response = await axios.post(`${API_BASE_URL}/recipes/${recipeId}/calculate-cost`, {
                        ingredients: scaledIngredients,
                        userId: userID,
                        preferredRegion
                    });
                    console.log('🔎 calculate-cost debug:', {
                        debugRegion: response.data?.debugRegion,
                        debugCollection: response.data?.debugCollection,
                        debugRegionSource: response.data?.debugRegionSource,
                        debugRequestPreferredRegion: response.data?.debugRequestPreferredRegion,
                        debugUserPreferredRegion: response.data?.debugUserPreferredRegion
                    });
                    
                    setRecipeCosts(prev => ({ ...prev, [recipeId]: response.data.totalCost }));
                    setIngredientPrices(response.data.ingredientPrices);
                    setLoadingCost(false);
                    setShowCosts(true); // Show costs when auto-fetched
                    
                    // Scroll to cost calculator header after DOM updates
                    requestAnimationFrame(() => {
                        setTimeout(() => {
                            scrollToCostCalculator();
                        }, 200);
                    });
                } catch (error) {
                    setLoadingCost(false);
                    console.error("Error auto-fetching recipe cost:", error);
                }
            };
            
            // Use a small delay to avoid race conditions
            const timer = setTimeout(() => {
                autoFetchCosts();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [recipe, recipeId, searchParams, loadingCost, recipeCosts, servingSizes, userID]);

    const isRecipeSaved = (id) => {
        return savedRecipes?.some(sid => sid.toString() === (id || recipeId).toString());
    };

    const isInMealPrepDraft = (id) => {
        return mealPrepDraftIds?.some(mid => mid.toString() === (id || recipeId).toString());
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this recipe?")) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/recipes/${recipeId}`, {
                data: { userID }
            });
            alert("Recipe deleted successfully!");
            navigate("/");
        } catch (error) {
            console.error("Error deleting recipe:", error);
            alert(error.response?.data?.message || "Error deleting recipe");
        }
    };

    const handleEdit = () => {
        navigate(`/edit-recipe/${recipeId}`);
    };

    const saveRecipe = async () => {
        try {
            if (!userID || !getAuthToken()) {
                return;
            }
            const response = await axios.put(`${API_BASE_URL}/recipes/save`,
                { recipeID: recipeId, userID },
                { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
            );
            setSavedRecipes(prev => [...prev, recipeId.toString()]);
            alert(response.data.message || "Recipe saved successfully!");
        } catch (error) {
            console.error("Error saving recipe:", error);
            alert(error.response?.data?.message || "Error saving recipe");
        }
    };

    const unsaveRecipe = async () => {
        try {
            if (!userID || !getAuthToken()) {
                return;
            }
            await axios.put(`${API_BASE_URL}/recipes/unsave`,
                { recipeID: recipeId, userID },
                { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
            );
            setSavedRecipes(prev => prev.filter(id => id.toString() !== recipeId.toString()));
            alert("Recipe unsaved successfully!");
        } catch (error) {
            console.error("Error unsaving recipe:", error);
            alert(error.response?.data?.message || "Error unsaving recipe");
        }
    };

    const addToMealPrepDraft = async () => {
        try {
            if (!userID || !getAuthToken()) return;
            await axios.put(`${API_BASE_URL}/recipes/meal-prep-draft/add`,
                { recipeID: recipeId, userID },
                { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
            );
            setMealPrepDraftIds(prev => prev.includes(recipeId.toString()) ? prev : [...prev, recipeId.toString()]);
            alert("Added to meal prep draft!");
        } catch (error) {
            console.error("Error adding to meal prep draft:", error);
            alert(error.response?.data?.message || "Error adding to meal prep draft");
        }
    };

    const removeFromMealPrepDraft = async () => {
        try {
            if (!userID || !getAuthToken()) return;
            await axios.put(`${API_BASE_URL}/recipes/meal-prep-draft/remove`,
                { recipeID: recipeId, userID },
                { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
            );
            setMealPrepDraftIds(prev => prev.filter(id => id !== recipeId.toString()));
            alert("Removed from meal prep draft!");
        } catch (error) {
            console.error("Error removing from meal prep draft:", error);
            alert(error.response?.data?.message || "Error removing from meal prep draft");
        }
    };

    const handleServingChange = (newServings) => {
        setServingSizes(prev => ({
            ...prev,
            [recipeId]: parseInt(newServings)
        }));
        
        // Remove any existing calculated cost so the cost section does not render
        // User must explicitly click "Calculate Cost" to recalculate after changing servings
        setRecipeCosts(prev => {
            const next = { ...prev };
            delete next[recipeId];
            return next;
        });

        // Clear ingredient prices cache to avoid stale data
        setIngredientPrices({});
    };

    const fetchRecipeCost = async (event) => {
        // Prevent any default behavior that might cause scrolling
        if (event) {
            event.preventDefault();
        }
        
        try {
            setLoadingCost(true);
            
            const currentServings = servingSizes[recipeId] || recipe.servings;
            const scaledIngredients = recipe.ingredients.map(ing => ({
                ...ing,
                amount: parseFloat(((ing.amount * currentServings) / recipe.servings).toFixed(2))
            }));

            console.log('Requesting cost calculation from backend...');
            const preferredRegion = window.localStorage.getItem('preferredRegion') || null;
            const response = await axios.post(`${API_BASE_URL}/recipes/${recipeId}/calculate-cost`, {
                ingredients: scaledIngredients,
                userId: userID,  // Always send userId to prioritize user preferences
                preferredRegion
            });

            // Set the data directly as received from the backend without additional processing
            console.log('Setting recipeCosts with totalCost:', response.data.totalCost);
            console.log('Setting ingredientPrices with keys:', Object.keys(response.data.ingredientPrices || {}));
            console.log('🔎 calculate-cost debug:', {
                debugRegion: response.data?.debugRegion,
                debugCollection: response.data?.debugCollection,
                debugRegionSource: response.data?.debugRegionSource,
                debugRequestPreferredRegion: response.data?.debugRequestPreferredRegion,
                debugUserPreferredRegion: response.data?.debugUserPreferredRegion
            });
            
            setRecipeCosts(prev => ({ ...prev, [recipeId]: response.data.totalCost }));
            setIngredientPrices(response.data.ingredientPrices);
            setLoadingCost(false);
            setShowCosts(true); // Show costs when they're calculated
            console.log('Successfully received and set cost data from backend');
            
            // Update URL to preserve state when navigating away
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('showCosts', 'true');
            setSearchParams(newSearchParams, { replace: true });
            
            // Scroll to cost calculator header after DOM updates
            requestAnimationFrame(() => {
                setTimeout(() => {
                    scrollToCostCalculator();
                }, 200);
            });
            
        } catch (error) {
            setLoadingCost(false);
            console.error("Error fetching recipe cost:", error);
            alert("Error calculating recipe cost");
        }
    };


    const saveAllPreferences = async () => {
        try {
            if (!userID) {
                alert("Please log in to save preferences");
                return;
            }
            
            setIsSaving(true);
            console.log("Starting to save preferences...");
            
            // Find the RecipePriceDetails component to get its current selections
            // We need to get the selections from the RecipePriceDetails component
            // which are stored in its selectedBrands state
            
            // Get a reference to the RecipePriceDetails component by checking
            // for the presence of the brand-select elements
            const brandSelects = document.querySelectorAll('.brand-select');
            const currentSelections = {};
            let selectionCount = 0;
            
            if (brandSelects.length > 0) {
                console.log("Found brand selection dropdowns in the UI");
                
                // For each dropdown, get the currently selected value
                brandSelects.forEach(select => {
                    const ingredientElement = select.closest('.ingredient-price-item').querySelector('.ingredient-name');
                    if (!ingredientElement) return;
                    
                    const ingredientName = ingredientElement.textContent;
                    const selectedBrandId = select.value;
                    const selectedOption = select.options[select.selectedIndex];
                    
                    // Get additional data from data attributes
                    const brandName = selectedOption?.dataset?.name;
                    const brandBrand = selectedOption?.dataset?.brand;
                    
                    console.log(`Read from dropdown - Ingredient: [${ingredientName}]`);
                    console.log(`  Selected brand ID: [${selectedBrandId}]`);
                    console.log(`  Selected brand name: [${brandName}]`);
                    console.log(`  Selected brand brand: [${brandBrand}]`);
                    
                    if (ingredientName && selectedBrandId && ingredientName.trim() !== '' && selectedBrandId !== 'none') {
                        // Find the selected brand details to log
                        const ingredientData = ingredientPrices[ingredientName];
                        const selectedBrand = ingredientData?.availableBrands?.find(b => b.id === selectedBrandId);
                        
                        if (selectedBrand) {
                            currentSelections[ingredientName.trim()] = selectedBrandId;
                            console.log(`Found matching brand: ${selectedBrand.name} (${selectedBrand.brand}) with ID: ${selectedBrandId}`);
                            selectionCount++;
                        } else {
                            console.log(`Could not find brand with ID ${selectedBrandId} in available brands for ${ingredientName}`);
                            // Log available brands for debugging
                            console.log('Available brands:', ingredientData?.availableBrands?.map(b => `${b.name} (${b.id})`));
                        }
                    } else {
                        console.log(`Skipping ingredient [${ingredientName}] with brand ID [${selectedBrandId}] - Invalid data`);
                    }
                });
            } else {
                console.log("No brand selection dropdowns found, using ingredient prices data");
                
                // Fall back to ingredientPrices if no dropdowns are found
                Object.entries(ingredientPrices).forEach(([ingredientName, data]) => {
                    if (data.selectedBrand && data.selectedBrand.id) {
                        // Make sure the ingredient name is valid
                        if (ingredientName && ingredientName.trim() !== '') {
                            currentSelections[ingredientName.trim()] = data.selectedBrand.id;
                            console.log(`Saving preference from data: ${ingredientName} = ${data.selectedBrand.name} (${data.selectedBrand.id})`);
                            selectionCount++;
                        }
                    }
                });
            }
            
            // Early return if no selections to save
            if (selectionCount === 0) {
                alert("No ingredient selections to save");
                setIsSaving(false);
                return;
            }
            
            console.log(`Saving ${selectionCount} preferences...`);
            console.log('Current selections:', JSON.stringify(currentSelections, null, 2));
            
            // Get current serving size for scaling
            const currentServings = servingSizes[recipeId] || recipe.servings;
            
            // Scale ingredients based on serving size
            const scaledIngredients = recipe.ingredients.map(ing => ({
                ...ing,
                amount: parseFloat(((ing.amount * currentServings) / recipe.servings).toFixed(2))
            }));
            
            // Simple API call to save preferences
            console.log('Sending request to save preferences...');
            const response = await axios.post(
                `${API_BASE_URL}/recipes/${recipeId}/calculate-cost`,
                {
                    ingredients: scaledIngredients,
                    userId: userID,
                    preferredRegion: window.localStorage.getItem('preferredRegion') || null,
                    saveAllSelections: true,  // Flag to save preferences
                    currentSelections,        // The selections to save
                    updateMachineLearning: true,
                    skipRecalculation: true   // Skip recalculation to just save
                }
            );
            
            console.log('Response from server:', response.data);
            
            if (response.data.success) {
                const count = response.data.preferencesUpdated || 'all';
                alert(`Successfully saved ${count} ingredient preferences!`);
                
                // After successful save, refresh the recipe costs to show the updated preferences
                await fetchRecipeCost();
            } else {
                alert("Error saving preferences: " + (response.data.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error saving preferences:", error);
            console.error("Error details:", error.response?.data || error.message);
            alert("Error saving preferences. Please try again later.");
        } finally {
            setIsSaving(false);
        }
    };


    // Modal handlers for image viewing
    const openImageModal = (imageUrl, altText) => {
        setModalImageUrl(imageUrl);
        setModalImageAlt(altText);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setModalImageUrl('');
        setModalImageAlt('');
    };

    // Toggle ingredient in cupboard (add or remove)
    const handleToggleCupboard = async (ingredient, index) => {
        const token = getAuthToken();
        
        if (!userID || !token) {
            openSignInPopup();
            return;
        }

        const isCurrentlyAdded = addedToCupboard[index];

        // Update UI immediately for instant feedback
        if (isCurrentlyAdded) {
            setAddedToCupboard(prev => {
                const next = { ...prev };
                delete next[index];
                return next;
            });
        } else {
            setAddedToCupboard(prev => ({ ...prev, [index]: true }));
        }

        try {
            if (isCurrentlyAdded) {
                // Remove from cupboard - find the cupboard item by ingredient name
                const ingredientName = ingredient.name;
                
                // First, get all cupboard items to find the ID
                const cupboardResponse = await axios.get(
                    `${API_BASE_URL}/cupboard/${userID}`,
                    {
                        headers: getAuthHeaders()
                    }
                );
                
                // Find the item that matches this ingredient
                const cupboardItem = cupboardResponse.data.items?.find(
                    item => item.ingredientName.toLowerCase() === ingredientName.toLowerCase()
                );
                
                if (cupboardItem) {
                    // Delete the item from cupboard
                    await axios.delete(
                        `${API_BASE_URL}/cupboard/${cupboardItem._id}`,
                        {
                            headers: getAuthHeaders()
                        }
                    );
                } else {
                    // If item not found, already removed - no need to do anything
                }
            } else {
                // Add to cupboard
                const currentServings = servingSizes[recipeId] || recipe.servings;
                const scaledAmount = parseFloat(
                    ((ingredient.amount * currentServings) / recipe.servings).toFixed(2)
                );
                
                // Determine the unit to use
                const unit = ingredient.unit === 'other' ? (ingredient.customUnit || 'pieces') : ingredient.unit;
                
                // Use ingredient name (not displayText) for consistency
                const ingredientName = ingredient.name;
                
                const response = await axios.post(
                    `${API_BASE_URL}/cupboard/add`,
                    {
                        ingredientName,
                        amount: scaledAmount,
                        unit,
                        location: 'cupboard'
                    },
                    {
                        headers: {
                            ...getAuthHeaders(),
                            'Content-Type': 'application/json'
                        }
                    }
                );
                
                // Success - UI already updated
            }
        } catch (error) {
            console.error("Error toggling ingredient in cupboard:", error);
            
            // Revert the UI change on error
            if (isCurrentlyAdded) {
                setAddedToCupboard(prev => ({ ...prev, [index]: true }));
            } else {
                setAddedToCupboard(prev => {
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
            }
            
            // Handle 401 unauthorized - token might be expired
            if (error.response?.status === 401) {
                alert("Your session has expired. Please log in again.");
                navigate(`/auth?redirect=${encodeURIComponent(`/recipe/${recipeId}`)}`);
                return;
            }
            
            const errorMessage = error.response?.data?.message || "Error updating cupboard";
            alert(errorMessage);
        }
    };

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && showImageModal) {
                closeImageModal();
            }
        };

        if (showImageModal) {
            document.addEventListener('keydown', handleEscapeKey);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
            document.body.style.overflow = 'unset';
        };
    }, [showImageModal]);

    if (error) {
        const isNotFound = /not found/i.test(error);
        return (
        <div className="recipe-error-container">
            <div className="error-content">
                <div className="error-icon" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isNotFound ? (
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        ) : (
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        )}
                    </svg>
                </div>
                <h2 className="error-title">{isNotFound ? 'Recipe not found' : 'Something went wrong'}</h2>
                <p className="error-message">
                    {isNotFound
                        ? 'This recipe may be private, removed, or the link might be incorrect.'
                        : error}
                </p>
                <button 
                    onClick={() => navigate('/')}
                    className="error-return-button"
                >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Return to home
                </button>
            </div>
        </div>
        );
    }

    if (!recipe) return (
        <div className="recipe-loading-container">
            <LoadingSpinner 
                size="xl" 
                color="primary" 
                text="Loading delicious recipe details..." 
            />
        </div>
    );

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div className="modern-recipe-details">
            {/* Fixed Back Button Navbar */}
            <div className="recipe-back-navbar">
                <button 
                    onClick={handleBack}
                    className="recipe-back-button"
                    aria-label="Go back"
                >
                    <svg className="back-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="back-text">Back</span>
                </button>
            </div>
            
            {/* Modern Hero Section */}
            <div className="recipe-hero">
                <div className="container">
                    <div className="recipe-hero-content">
                        {/* Recipe Image */}
                        <div className="recipe-image-section">
                            <RecipeImage 
                                imageUrl={recipe.imageUrl} 
                                alt={recipe.name}
                                className="recipe-main-image"
                            />
                            
                            {/* Action Buttons Overlay - plus opens save options popup */}
                            <div className="recipe-actions-overlay">
                                {userID && (
                                    <button
                                        onClick={() => setShowSaveOptionsPopup(true)}
                                        className={`action-button-plus ${isRecipeSaved(recipeId) ? 'saved' : ''}`}
                                        aria-label="Save or add to meal prep draft"
                                    >
                                        <PlusIcon
                                            filled={isRecipeSaved(recipeId)}
                                            className="w-6 h-6"
                                        />
                                    </button>
                                )}
                                
                                {recipe.userOwner === userID && (
                                    <div className="owner-actions">
                                        <button 
                                            onClick={handleEdit}
                                            className="action-button-edit"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            Edit
                                        </button>
                                        <button 
                                            onClick={handleDelete}
                                            className="action-button-delete"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Recipe Info */}
                        <div className="recipe-info">
                            <div className="recipe-meta">
                                <div className="recipe-author">
                                    <div className="author-avatar">
                                        <ProfilePicture 
                                            src={recipe.userProfilePicture}
                                            alt={recipe.createdBy || 'Recipe Creator'}
                                            className="w-8 h-8"
                                        />
                                    </div>
                                    <div className="author-info">
                                        <span className="author-name">{recipe.createdBy || 'Mob'}</span>
                                        <span className="author-label">Recipe Creator</span>
                                    </div>
                                </div>
                                
                                <div className="recipe-stats">
                                    <div className="stat-item">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span>{recipe.cookingTime} mins</span>
                                    </div>
                                    <div className="stat-item">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        <span>{recipe.servings} servings</span>
                                    </div>
                                </div>
                            </div>

                            <h1 className="recipe-title">{recipe.name}</h1>
                            
                            {/* Dietary Badges */}
                            <div className="dietary-badges">
                                {recipe.isVegan && (
                                    <span className="dietary-badge vegan">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                        </svg>
                                        Vegan
                                    </span>
                                )}
                                {recipe.isVegetarian && !recipe.isVegan && (
                                    <span className="dietary-badge vegetarian">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                        </svg>
                                        Vegetarian
                                    </span>
                                )}
                                {recipe.categories && recipe.categories.map(category => {
                                    // Map category values to display names
                                    const categoryDisplayNames = {
                                        'flavoured chicken': 'Flavoursome Chicken',
                                        'break a egg breakfasts': 'Break-a-Egg Breakfasts',
                                        'cape malay indian': 'Cape Malay & Indian Spice',
                                        'south african': 'Proudly South African',
                                        'baked goods': 'Oven-Fresh Baked Goods',
                                        'braai & side dishes': 'Braai & Sides',
                                        'vegetarian': 'Vegetarian',
                                        'decadently healthy desserts': 'Decadently Healthy Desserts',
                                        'healthy classics': 'Healthy Classics'
                                    };
                                    
                                    const displayName = categoryDisplayNames[category.toLowerCase()] || 
                                        category.charAt(0).toUpperCase() + category.slice(1);
                                    
                                    return (
                                        <span key={category} className="category-badge">
                                            {displayName}
                                        </span>
                                    );
                                })}
                            </div>

                            {/* Compact Nutritional Info */}
                            {nutritionData && nutritionData.perServing && (
                                <div className="recipe-nutrition-compact">
                                    <button 
                                        className="nutrition-compact-header"
                                        onClick={() => setShowNutrition(!showNutrition)}
                                        aria-expanded={showNutrition}
                                    >
                                        <div className="nutrition-compact-title">Nutrition per serving</div>
                                        <svg 
                                            className={`nutrition-toggle-icon ${showNutrition ? 'expanded' : ''}`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {showNutrition && (
                                        <div className="nutrition-compact-grid">
                                        <div className="nutrition-compact-item">
                                            <div className="nutrition-icon">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                </svg>
                                            </div>
                                            <div className="nutrition-compact-content">
                                                <div className="nutrition-compact-value">{formatNutritionValue('Calories', nutritionData.perServing.Calories)}</div>
                                                <div className="nutrition-compact-label">Calories</div>
                                            </div>
                                        </div>
                                        <div className="nutrition-compact-item">
                                            <div className="nutrition-icon">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                </svg>
                                            </div>
                                            <div className="nutrition-compact-content">
                                                <div className="nutrition-compact-value">{formatNutritionValue('Protein', nutritionData.perServing.Protein)}</div>
                                                <div className="nutrition-compact-label">Protein</div>
                                            </div>
                                        </div>
                                        <div className="nutrition-compact-item">
                                            <div className="nutrition-icon">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                            </div>
                                            <div className="nutrition-compact-content">
                                                <div className="nutrition-compact-value">{formatNutritionValue('Carbs', nutritionData.perServing.Carbs)}</div>
                                                <div className="nutrition-compact-label">Carbs</div>
                                            </div>
                                        </div>
                                        <div className="nutrition-compact-item">
                                            <div className="nutrition-icon">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                                </svg>
                                            </div>
                                            <div className="nutrition-compact-content">
                                                <div className="nutrition-compact-value">{formatNutritionValue('Fats', nutritionData.perServing.Fats)}</div>
                                                <div className="nutrition-compact-label">Fats</div>
                                            </div>
                                        </div>
                                        <div className="nutrition-compact-item">
                                            <div className="nutrition-icon">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                </svg>
                                            </div>
                                            <div className="nutrition-compact-content">
                                                <div className="nutrition-compact-value">{formatNutritionValue('Sugar', nutritionData.perServing.Sugar)}</div>
                                                <div className="nutrition-compact-label">Sugar</div>
                                            </div>
                                        </div>
                                        <div className="nutrition-compact-item">
                                            <div className="nutrition-icon">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                                </svg>
                                            </div>
                                            <div className="nutrition-compact-content">
                                                <div className="nutrition-compact-value">{formatNutritionValue('Sodium', nutritionData.perServing.Sodium)}</div>
                                                <div className="nutrition-compact-label">Sodium</div>
                                            </div>
                                        </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Detailed Nutritional Breakdown */}
                            {nutritionData && nutritionData.ingredientBreakdown && nutritionData.ingredientBreakdown.length > 0 && (
                                <div className="recipe-nutrition-breakdown">
                                    <button 
                                        className="nutrition-breakdown-header"
                                        onClick={() => setShowNutritionBreakdown(!showNutritionBreakdown)}
                                        aria-expanded={showNutritionBreakdown}
                                    >
                                        <div className="nutrition-breakdown-title">Detailed Nutritional Breakdown</div>
                                        <svg 
                                            className={`nutrition-toggle-icon ${showNutritionBreakdown ? 'expanded' : ''}`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {showNutritionBreakdown && (
                                        <div className="nutrition-breakdown-content">
                                            {nutritionData.ingredientBreakdown.map((ingredient, index) => (
                                                <div key={index} className="breakdown-ingredient-item">
                                                    <div className="breakdown-ingredient-header">
                                                        <div className="breakdown-ingredient-name">
                                                            {ingredient.name}
                                                        </div>
                                                        <div className="breakdown-ingredient-amount">
                                                            {ingredient.amount} {ingredient.unit === 'other' ? (ingredient.customUnit || 'unit') : ingredient.unit}
                                                            {ingredient.amountInGrams && ingredient.unit !== 'g' && ingredient.unit !== 'other' && (
                                                                <span className="breakdown-grams"> ({ingredient.amountInGrams.toFixed(1)}g)</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {ingredient.total ? (
                                                        <div className="breakdown-nutrition-section">
                                                            <div className="breakdown-section-title">Total Nutritional Values</div>
                                                            <div className="breakdown-values">
                                                                <div className="breakdown-value-item">
                                                                    <span className="breakdown-label">Calories:</span>
                                                                    <span className="breakdown-value">{formatNutritionValue('Calories', ingredient.total.Calories)}</span>
                                                                </div>
                                                                <div className="breakdown-value-item">
                                                                    <span className="breakdown-label">Protein:</span>
                                                                    <span className="breakdown-value">{formatNutritionValue('Protein', ingredient.total.Protein)}</span>
                                                                </div>
                                                                <div className="breakdown-value-item">
                                                                    <span className="breakdown-label">Carbs:</span>
                                                                    <span className="breakdown-value">{formatNutritionValue('Carbs', ingredient.total.Carbs)}</span>
                                                                </div>
                                                                <div className="breakdown-value-item">
                                                                    <span className="breakdown-label">Fats:</span>
                                                                    <span className="breakdown-value">{formatNutritionValue('Fats', ingredient.total.Fats)}</span>
                                                                </div>
                                                                <div className="breakdown-value-item">
                                                                    <span className="breakdown-label">Sugar:</span>
                                                                    <span className="breakdown-value">{formatNutritionValue('Sugar', ingredient.total.Sugar)}</span>
                                                                </div>
                                                                <div className="breakdown-value-item">
                                                                    <span className="breakdown-label">Sodium:</span>
                                                                    <span className="breakdown-value">{formatNutritionValue('Sodium', ingredient.total.Sodium)}</span>
                                                                </div>
                                                                <div className="breakdown-value-item">
                                                                    <span className="breakdown-label">Fiber:</span>
                                                                    <span className="breakdown-value">{formatNutritionValue('Fiber', ingredient.total.Fiber)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="breakdown-no-data">
                                                            No nutritional data available for this ingredient
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modern Serving Size Selector */}
            {/* <div className="modern-serving-section">
                <div className="container">
                    <ServingSizeSelector
                        baseServings={recipe.servings}
                        currentServings={servingSizes[recipeId] || recipe.servings}
                        onChange={handleServingChange}
                        onServingSizeConfirm={handleServingChange}
                    />
                </div>
            </div> */}

            {/* Modern Ingredients Section */}
            <div className="modern-ingredients-section">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">Ingredients</h2>
                        <button 
                            className="toggle-button"
                            onClick={() => setShowIngredients(!showIngredients)}
                            aria-expanded={showIngredients}
                        >
                            <span className="toggle-text">{showIngredients ? 'Hide' : 'Show'} Ingredients</span>
                            <svg className={`toggle-icon ${showIngredients ? 'expanded' : 'collapsed'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                    
                    {showIngredients && (
                        <div className="ingredients-content">
                            {/* Ingredients Photo */}
                            {recipe.ingredientsPhotoUrl && (
                                <div className="ingredients-photo-section">
                                    <h3 className="photo-title">Ingredients Overview</h3>
                                    <div className="photo-container">
                                        <img 
                                            src={recipe.ingredientsPhotoUrl} 
                                            alt="All ingredients for this recipe"
                                            onClick={() => openImageModal(recipe.ingredientsPhotoUrl, "All ingredients for this recipe")}
                                            className="ingredients-photo"
                                        />
                                        <p className="photo-caption">Click to view full size</p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Ingredients List */}
                            <div className="ingredients-grid">
                                {recipe.ingredients && recipe.ingredients.map((ingredient, index) => {
                                    const currentServings = servingSizes[recipeId] || recipe.servings;
                                    const adjustedAmount = (
                                        (ingredient.amount * currentServings) / recipe.servings
                                    ).toFixed(2);
                                    
                                    // Helper function to format display text with scaled amounts
                                    const formatDisplayText = () => {
                                        if (ingredient.displayText) {
                                            // If display text exists, try to scale the number in it if possible
                                            const servingRatio = currentServings / recipe.servings;
                                            
                                            // Match numbers at the start of the display text (e.g., "2 onions" -> "4 onions")
                                            let displayWithScaling = ingredient.displayText.replace(
                                                /^(\d+\.?\d*)\s+/,
                                                (match, number) => {
                                                    const scaledNum = (parseFloat(number) * servingRatio).toFixed(2).replace(/\.?0+$/, '');
                                                    return `${scaledNum} `;
                                                }
                                            );
                                            
                                            // If displayText doesn't already include preparation info, and we have a separate preparation field,
                                            // append it to the display text
                                            if (ingredient.preparation) {
                                                const prepLower = ingredient.preparation.toLowerCase();
                                                const displayLower = displayWithScaling.toLowerCase();
                                                
                                                // Check if preparation is already mentioned in displayText
                                                const prepAlreadyIncluded = displayLower.includes(prepLower) || 
                                                    displayLower.includes('chopped') || displayLower.includes('diced') || 
                                                    displayLower.includes('minced') || displayLower.includes('sliced') ||
                                                    displayLower.includes('julienned') || displayLower.includes('crushed') ||
                                                    displayLower.includes('grated') || displayLower.includes('shredded') ||
                                                    displayLower.includes('peeled') || displayLower.includes('cubed') ||
                                                    displayLower.includes('in pieces');
                                                
                                                if (!prepAlreadyIncluded) {
                                                    displayWithScaling += `, ${ingredient.preparation}`;
                                                }
                                            }
                                            
                                            return displayWithScaling;
                                        }
                                        return null;
                                    };
                                    
                                    const displayText = formatDisplayText();
                                    
                                    const isAdding = addingToCupboard[index];
                                    const isAdded = addedToCupboard[index];
                                    
                                    return (
                                        <div key={index} className="ingredient-card">
                                            <div className="ingredient-content">
                                                {displayText ? (
                                                    // Show human-readable format (includes preparation if not already in displayText)
                                                    <div className="ingredient-display-text">
                                                        {displayText}
                                                    </div>
                                                ) : (
                                                    // Fall back to structured format with preparation if available
                                                    <>
                                                        <div className="ingredient-amount">
                                                            <span className="amount">{adjustedAmount}</span>
                                                            <span className="unit">{ingredient.unit === 'other' ? ingredient.customUnit : ingredient.unit}</span>
                                                        </div>
                                                        <div className="ingredient-name">
                                                            {ingredient.name}
                                                            {ingredient.preparation && <span className="ingredient-prep"> ({ingredient.preparation})</span>}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {/* Add to Cupboard Checkbox - show for all; non-logged-in users get SignInPopup on click */}
                                            <button
                                                className={`add-to-cupboard-btn ${isAdded ? 'added' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleToggleCupboard(ingredient, index);
                                                }}
                                                title={isAdded ? "Remove from cupboard" : "Add to cupboard"}
                                                aria-label={isAdded ? "Remove from cupboard" : "Add to cupboard"}
                                                type="button"
                                            >
                                                <svg className="cupboard-plus-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M4 12h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modern Instructions Section */}
            <div className="modern-instructions-section">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">Instructions</h2>
                        <button 
                            className="toggle-button"
                            onClick={() => setShowInstructions(!showInstructions)}
                            aria-expanded={showInstructions}
                        >
                            <span className="toggle-text">{showInstructions ? 'Hide' : 'Show'} Instructions</span>
                            <svg className={`toggle-icon ${showInstructions ? 'expanded' : 'collapsed'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                    
                    {showInstructions && (
                        <div className="instructions-content">
                            {/* Instructional Roadmap Section */}
                            {recipe.instructionalRoadmap && recipe.instructionalRoadmap.steps && recipe.instructionalRoadmap.steps.length > 0 && (
                                <div className="roadmap-section">
                                    <div className="roadmap-header">
                                        <h3 className="roadmap-title">
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                                            </svg>
                                            Visual Step-by-Step Guide
                                        </h3>
                                        <div className="roadmap-meta">
                                            <span className="meta-item">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                                </svg>
                                                {recipe.instructionalRoadmap.metadata?.imageCount || 0} images
                                            </span>
                                            <span className="meta-item">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                </svg>
                                                {recipe.instructionalRoadmap.metadata?.totalSteps || 0} steps
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="roadmap-steps">
                                        {recipe.instructionalRoadmap.steps.map((roadmapStep, index) => (
                                            <div key={index} className="roadmap-step-card">
                                                <div className="step-number">{roadmapStep.stepNumber}</div>
                                                <div className="step-content">
                                                    {roadmapStep.imageUrl && (
                                                        <div className="step-image">
                                                            <img 
                                                                src={roadmapStep.imageUrl} 
                                                                alt={`Step ${roadmapStep.stepNumber}`}
                                                                onClick={() => openImageModal(roadmapStep.imageUrl, `Step ${roadmapStep.stepNumber}`)}
                                                                className="step-image-img"
                                                            />
                                                            <p className="image-caption">Click to view full size</p>
                                                        </div>
                                                    )}
                                                    <div className="step-instruction">
                                                        {roadmapStep.enhancedInstruction}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Traditional Instructions Section */}
                            <div className="traditional-instructions">
                                <h3 className="instructions-subtitle">Step-by-Step Instructions</h3>
                                <div className="instructions-list">
                                    {recipe.instructions && recipe.instructions.map((step, index) => (
                                        <div key={index} className="instruction-step">
                                            <div className="step-number">{index + 1}</div>
                                            <div className="step-content">
                                                <div className="step-text">
                                                    {typeof step === 'string' ? step : step.text}
                                                </div>
                                                {typeof step === 'object' && step.imageUrl && step.imageUrl !== '' && (
                                                    <div className="step-image">
                                                        <img 
                                                            src={step.imageUrl} 
                                                            alt={`Step ${index + 1}`}
                                                            onClick={() => openImageModal(step.imageUrl, `Step ${index + 1}`)}
                                                            className="step-image-img"
                                                        />
                                                        <p className="image-caption">Click to view full size</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modern Cost Calculation Section */}
            <div className="modern-cost-section">
                <div className="container">
                    <div className="cost-header" id="recipe-cost-calculator-header">
                        <h2 className="section-title">Recipe Cost Calculator</h2>
                        <p className="cost-description">Get accurate pricing based on current ingredient costs</p>
                    </div>
                    
                    <div className="cost-actions">
                        <button 
                            onClick={(e) => {
                                e.preventDefault();
                                fetchRecipeCost(e);
                            }}
                            className="calculate-cost-button-modern"
                            disabled={loadingCost}
                            type="button"
                        >
                            {loadingCost ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Calculating...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                    Calculate Cost
                                </>
                            )}
                        </button>
                        
                        {!loadingCost && recipeCosts.hasOwnProperty(recipeId) && userID && (
                            <button 
                                onClick={saveAllPreferences}
                                className="save-preferences-button-modern"
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                        </svg>
                                        Save Preferences
                                    </>
                                )}
                            </button>
                        )}
                        
                        {!loadingCost && recipeCosts.hasOwnProperty(recipeId) && (
                            <button 
                                onClick={() => setShowCosts(!showCosts)}
                                className="hide-costs-button-modern"
                                title={showCosts ? "Hide cost details" : "Show cost details"}
                            >
                                {showCosts ? (
                                    <>
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                        Hide Costs
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Show Costs
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Cost Results */}
                    {!loadingCost && recipeCosts.hasOwnProperty(recipeId) && showCosts && (
                        <div className="cost-results">
                            <div className="cost-summary">
                                <div className="total-cost">
                                    <span className="cost-label">Total Cost:</span>
                                    <span className="cost-amount">R{recipeCosts[recipeId].toFixed(2)}</span>
                                </div>
                                {ingredientPrices && Object.keys(ingredientPrices).length > 0 && (
                                    <div className="cost-breakdown">
                                        <span className="breakdown-label">Per serving: R{(recipeCosts[recipeId] / (servingSizes[recipeId] || recipe.servings)).toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="recipe-price-details-container" ref={recipePriceDetailsRef}>
                                <RecipePriceDetails 
                                    ingredients={recipe.ingredients.map(ing => ({
                                        ...ing,
                                        amount: (ing.amount * (servingSizes[recipeId] || recipe.servings)) / recipe.servings
                                    }))} 
                                    ingredientPrices={ingredientPrices}
                                    onTotalCostChange={(newTotalCost) => {
                                        setRecipeCosts(prev => ({ ...prev, [recipeId]: newTotalCost }));
                                    }}
                                    onRequireSignIn={openSignInPopup}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <RecipeSuggestions
                recipeId={recipeId}
                ingredients={recipe.ingredients.map(ing => ({
                    ...ing,
                    amount: (ing.amount * (servingSizes[recipeId] || recipe.servings)) / recipe.servings
                }))}
                ingredientPrices={ingredientPrices}
            />

            <RecipeBuddy
                recipe={recipe}
                recipeId={recipeId}
                isAuthenticated={isAuthenticated}
                initialOpen={location.state?.openFrankie === true}
                onSignInRequired={() => {
                    setSignInPopupFromFrankie(true);
                    openSignInPopup();
                }}
            />

            {/* Image Modal */}
            {showImageModal && (
                <div 
                    className="image-modal-overlay"
                    onClick={closeImageModal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '20px'
                    }}
                >
                    <div 
                        className="image-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'relative',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        {/* Close button */}
                        <button
                            onClick={closeImageModal}
                            style={{
                                position: 'absolute',
                                top: '-40px',
                                right: '0',
                                background: 'rgba(255, 255, 255, 0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                color: 'white',
                                fontSize: '24px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10000,
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                            }}
                        >
                            ×
                        </button>

                        {/* Image */}
                        <img
                            src={modalImageUrl}
                            alt={modalImageAlt}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                            }}
                        />

                        {/* Image caption */}
                        <p style={{
                            color: 'white',
                            fontSize: '16px',
                            marginTop: '15px',
                            textAlign: 'center',
                            maxWidth: '600px',
                            lineHeight: '1.4'
                        }}>
                            {modalImageAlt}
                        </p>

                        {/* Instructions */}
                        <p style={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '14px',
                            marginTop: '10px',
                            textAlign: 'center',
                            fontStyle: 'italic'
                        }}>
                            Press ESC or click outside to close
                        </p>
                    </div>
                </div>
            )}

            {/* Save options popup: saved recipes + meal prep draft */}
            <SaveOptionsPopup
                show={showSaveOptionsPopup}
                onClose={() => setShowSaveOptionsPopup(false)}
                recipeId={recipeId}
                recipeName={recipe?.name}
                isSaved={isRecipeSaved(recipeId)}
                isInMealPrepDraft={isInMealPrepDraft(recipeId)}
                onAddToSaved={saveRecipe}
                onRemoveFromSaved={unsaveRecipe}
                onAddToMealPrepDraft={addToMealPrepDraft}
                onRemoveFromMealPrepDraft={removeFromMealPrepDraft}
            />

            <SignInPopup
                show={showSignInPopup}
                onClose={() => {
                    setSignInPopupFromFrankie(false);
                    handleCloseSignInPopup();
                }}
                subtitle={signInPopupFromFrankie ? 'You need to sign in to continue talking to Frankie.' : undefined}
            />

            
        </div>
    );
}

const styles = StyleSheet.create({
    errorContainer: {
        flex: 1,
        padding: 20,
        backgroundColor: '#fff3f3',
        alignItems: 'center',
        justifyContent: 'center',
    },
    errorText: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 10,
    },
    container: {
        flex: 1,
        padding: 20,
        backgroundColor: '#fff',
    },
    header: {
        marginBottom: 20,
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 10,
    },
    actionButtons: {
        gap: 10,
    },
    ownerButtons: {
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        gap: 10,
        marginBottom: 10,
    },
    button: {
        padding: 12,
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
    },
    editButton: {
        backgroundColor: '#28a745',
    },
    deleteButton: {
        backgroundColor: '#dc3545',
    },
    buttonText: {
        color: '#fff',
        fontWeight: '500',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    mainContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
    },
    uniformContainer: {
        width: '100%',
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        padding: 15,
        marginBottom: 10,
        boxSizing: 'border-box',
        ...Platform.select({
            web: {
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            },
            default: {
                elevation: 2
            }
        })
    },
    sectionTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 10,
    },
    ingredientsList: {
        marginBottom: 20,
    },
    ingredientItem: {
        fontSize: 16,
    },
    instructionsList: {
        marginBottom: 20,
    },
    instructionItem: {
        fontSize: 16,
        lineHeight: 24,
    },
    createdBy: {
        fontSize: 16,
        color: '#666',
        marginBottom: 10,
    },
    servings: {
        fontSize: 16,
        color: '#666',
        marginBottom: 10,
        fontWeight: '500'
    },
    recipeImage: {
        width: '100%',
        height: 300,
        objectFit: 'cover',
        borderRadius: '8px',
        marginBottom: '20px',
    },
    ingredientContainer: {
        backgroundColor: '#ffffff',
        padding: 15,
        marginBottom: 10,
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
        elevation: 2,
    },
    instructionContainer: {
        backgroundColor: '#ffffff',
        padding: 15,
        marginBottom: 10,
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
        elevation: 2,
    },
    recipeBadges: {
        flexDirection: 'row',
        gap: 10,
        marginBottom: 15,
    },
    badge: {
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 4,
        fontSize: 14,
        fontWeight: '500',
    },
    veganBadge: {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    vegetarianBadge: {
        backgroundColor: '#8BC34A',
        color: 'white',
    },
    recipeCategories: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 15,
    },
    categoryTag: {
        backgroundColor: '#f0f0f0',
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 16,
        fontSize: 14,
        color: '#666',
    },
});